import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { capitalizeFirstLetter } from '../../utils/string';
import { getColorFromCode } from '../../common/constants';
import { ContentBox, Maybe, SimpleContent, SimpleContentDescription, UniverseSections } from '../../generated/contentful-generated-types';
import { isContentBox, isSimpleContent } from '../../utils/contentful';
import { useRichText } from '../../hooks/richtext';
import EntryLink from '../EntryLink';

interface Props {
  section: UniverseSections;
}

type ExtendedSimpleContent = SimpleContent & {
  simpleContentDescription: SimpleContentDescription;
};

export const HighlightContentBoxes: FunctionComponent<Props> = ({ section }) => {
  const items = section?.itemsCollection?.items;

  const simpleContentItem = items?.find((item): item is ExtendedSimpleContent => !!item && isSimpleContent(item));

  // do not display anything if no simplecontent
  if (!simpleContentItem) {
    return null;
  }

  const contentBoxes = items?.filter((item): item is ContentBox => !!item && isContentBox(item)).slice(0, 2);

  const { title, simpleContentDescription: description, charterTheme } = simpleContentItem;

  const convertToTitleCase = (str: Maybe<string>) => {
    return str && str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <StyledSection>
      <StyledCardGrid>
        {simpleContentItem && (
          <StyledMainCardText charterTheme={charterTheme}>
            <StyledContent>
              <StyledTitle>{capitalizeFirstLetter(title || '')}</StyledTitle>
              <StyledDescription>{description && useRichText(description)}</StyledDescription>
            </StyledContent>
          </StyledMainCardText>
        )}
        {
          <StyledMainCardBoxes>
            {contentBoxes &&
              contentBoxes?.map((contentBox, index) => (
                <StyledBox key={index} entry={contentBox}>
                  <StyledBoxImage src={contentBox.thumbnailImage?.url} />
                  <StyledBoxText>
                    <StyledBoxTitle className="box-title">{contentBox.thumbnailTitle && convertToTitleCase(contentBox.thumbnailTitle)}</StyledBoxTitle>
                    <StyledBoxPrice></StyledBoxPrice>
                  </StyledBoxText>
                </StyledBox>
              ))}
          </StyledMainCardBoxes>
        }
      </StyledCardGrid>
    </StyledSection>
  );
};

const StyledSection = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  z-index: 1;

  @media (max-width: 692px) {
    padding-bottom: 0px;
  }
  @media (max-width: 380) {
    margin-top: 0px;
  }
`;

const StyledCardGrid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  gap: 24px;
  align-self: stretch;
  @media (max-width: 768px) {
    gap: 4px;
  }
  @media (max-width: 692px) {
    flex-direction: column;
    align-items: start;
    gap: 4px;
  }
`;

const StyledMainCardText = styled.div<{ mainCardPosition: string }>`
  display: flex;
  flex: 1;
  width: 50%;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;

  background-color: ${props => getColorFromCode(props.charterTheme, 'transparent')};
  @media (max-width: 692px) {
    gap: 4px;
    width: 100%;
    padding: 32px 24px;
  }
`;

const StyledContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 692px;
  flex-direction: column;
  padding: 0% 15%;
  @media (max-width: 576px) {
    padding: 0;
  }
`;

const StyledTitle = styled.div`
  font-size: 24px;
  font-family: 'Styrene Medium';
  line-height: 1.5;
  color: ${getColorFromCode('exa black', 'rgba(0, 200, 130, 0.3)')};
  margin-bottom: 8px;

  @media (max-width: 692px) {
  }
  @media (max-width: 692px) {
    font-size: 20px;
    left: 8px;
    top: 8px;
  }
`;

const StyledDescription = styled.div`
  margin-bottom: 24px;
  font-size: 24px;
  line-height: 1.5;
  font-size: 16px;
  @media (max-width: 692px) {
    font-size: 14px;
  }
`;

const StyledMainCardBoxes = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 24px;
  row-gap: 16px;
  @media (max-width: 768px) {
    gap: 4px;
  }
  @media (max-width: 692px) {
    gap: 4px;
    min-width: 100%;
  }
`;
const StyledBoxImage = styled.div<{ src: string }>`
  height: 196px;
  align-self: stretch;
  background-image: url(${props => props.src});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;

  @media (max-width: 692px) {
    height: 120px;
    align-self: stretch;
  }
`;
const StyledBoxText = styled.div`
  padding: 16px 8px;

  @media (max-width: 692px) {
    padding: 8px;
  }
`;

const StyledBoxTitle = styled.div`
  color: ${getColorFromCode('exa black', '#323232')};
  font-family: 'Styrene Medium';
  font-size: 16px;
  font-style: normal;
  line-height: normal;
  @media (max-width: 692px) {
    font-size: 14px;
  }
`;
const StyledBoxPrice = styled.div`
  color: ${getColorFromCode('exa black', '#323232')};
  font-family: 'Styrene Medium';
  font-size: 16px;
  font-weight: 700;
`;

const StyledBox = styled(EntryLink)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  transition: all 0.5s;

  &:hover {
    box-shadow: 5px 10px 15px -3px rgba(0, 0, 0, 0.1);
    position: relative;

    .box-title {
      color: ${getColorFromCode('exa pink', '#FF3D77')};
    }
  }
`;
